import { useEffect, useRef, useState } from 'react'
import { Button } from '@appic/ui-components'
import { clearUserCache } from '@appic/lib'
import { authClient } from '@appic/lib/auth/authClient'
import { useServiceLayerAuth } from '@appic/lib/auth/ServiceLayerAuthProvider'
import { createLogger } from '@appic/lib/utils/logger'

const logger = createLogger('SessionExpiredOverlay')

/**
 * Global overlay that appears when the session of a SIGNED-IN user is gone
 * (cookies expired, 401 returned, or the mount-time /v1/auth/session probe
 * fails). It is mounted at provider level, so it renders on every route —
 * INCLUDING the login screen. That makes the signed-in precondition
 * load-bearing, not cosmetic: see THE GATE below.
 *
 * Listens for TWO custom events:
 *
 * - 'service-layer:unauthorized' — dispatched by serviceLayerClient.ts on
 *   any 401 response during normal API traffic.
 *
 * - 'auth:session-expired' — dispatched by authCheckStatus.ts when the
 *   mount-time /v1/auth/session probe fails for a user who had email
 *   cached locally. Without this listener the dispatch was a dead event
 *   (only useAuthAuditTrail observed it), leaving the user staring at a
 *   silent-empty Tagesplanung surface — BUG-SL-SCOUT-REP-CANNOT-READ-OWN-
 *   MISSIONS-2026-05-26. Per D-33 R1: never render silent-empty when the
 *   session is actually gone.
 *
 * THE GATE (BUG-FE-SESSION-EXPIRED-OVERLAY-BLOCKS-LOGIN-2026-07-16, P0):
 * neither event may surface this overlay unless a user is actually SIGNED IN.
 * A 401 while you are already logged out is EXPECTED — it is not a session
 * expiry. Ungated, the login screen's own SL traffic 401'd, this overlay
 * covered the login form and ran authClient.logout() against it, and "Neu
 * anmelden" reloaded into an identical loop: the overlay stood on the door it
 * told the user to walk through.
 *
 * The signal is the ServiceLayerAuthProvider's `email` — the same value
 * authCheckStatus.ts calls `hadEmail` and already gates its own
 * 'auth:session-expired' dispatch on. Deliberately NOT the browser-storage key
 * `appic:currentUserEmail` (the condition this component's doc originally
 * named): nothing in the repo writes that key — the only email `setItem` is
 * agentSession.ts, which writes a different one — so gating on it would
 * suppress the overlay for everyone and delete the feature. Nor the Supabase
 * session key `sb-clanda-sl`: incident telemetry shows re-handoff SUCCEEDING
 * (i.e. a valid Supabase session present) while hadEmail was false, so that
 * key would not have discriminated the deadlock at all.
 *
 * Mounted once in Providers — protects all screens, not just PreQual.
 */
export function SessionExpiredOverlay() {
  const [expired, setExpired] = useState(false)
  const { email } = useServiceLayerAuth()

  // The listeners are registered once, but must read the CURRENT identity at
  // event time. A closure over `email` would freeze the mount-time value
  // (null on a cold load) and re-open the deadlock. authCheckStatus.ts holds
  // its own `emailRef` for exactly this reason.
  const emailRef = useRef(email)
  emailRef.current = email

  useEffect(() => {
    const handler = () => {
      const signedInAs = emailRef.current
      // THE GATE — no session to expire, nothing to announce, nothing to tear
      // down. Leave the login screen alone.
      if (signedInAs === null) return

      setExpired(true)
      // DSGVO: clear sensitive cached data on session expiry (ADR-037)
      clearUserCache(signedInAs).catch(err => {
        // Cache clear failing on session expiry is non-blocking — user is
        // re-auth'd on next page load, which redoes the clear. Log so
        // persistent clear failures surface in Sentry breadcrumbs.
        logger.warn('clearUserCache failed on session expiry', err)
      })
      // Mirror the logout teardown: run the full package logout so the
      // "Neu anmelden" reload can't re-handoff the expired user. authClient
      // .logout() clears the SL cookie AND the authoritative Supabase session
      // at sb-clanda-sl (PR #1770), so we never re-mint the cookie on reload.
      // Best-effort: the session is already gone, so failures are non-blocking.
      authClient.logout().catch(err => {
        logger.warn('authClient.logout failed on session expiry', err)
      })
    }
    window.addEventListener('service-layer:unauthorized', handler)
    window.addEventListener('auth:session-expired', handler)
    return () => {
      window.removeEventListener('service-layer:unauthorized', handler)
      window.removeEventListener('auth:session-expired', handler)
    }
  }, [])

  // `expired` must not outlive its cause. A recovered session (the provider
  // repopulating `email`, e.g. after a successful re-handoff) takes the modal
  // down instead of stranding the user behind a stale one until a reload.
  // Only a null -> signed-in transition can fire this: during a genuine expiry
  // `email` goes the other way (or is unchanged), so the overlay stands.
  useEffect(() => {
    if (email !== null) setExpired(false)
  }, [email])

  if (!expired) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/70 p-4">
      <div className="max-w-sm space-y-4 rounded-xl border border-border bg-card p-8 text-center">
        <h2 className="text-lg font-bold">Sitzung abgelaufen</h2>
        <p className="text-sm text-muted-foreground">
          Deine Anmeldung ist abgelaufen. Bitte melde dich erneut an.
          Deine Eingaben wurden lokal gesichert.
        </p>
        <Button
          className="w-full"
          onClick={() => window.location.reload()}
        >
          Neu anmelden
        </Button>
      </div>
    </div>
  )
}
